import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import CustomLegend from "./CustomLegend";
import { RiskTypes } from "../../type/enums";

type RiskData = {
  [key in RiskTypes]: number;
};

interface ChartData extends RiskData {
  xLabel1: string;
  xLabel: string;
}

interface LineChartContainerProps {
  data: ChartData[];
}

const riskTypeColors: Partial<Record<RiskTypes, string>> = {
  [RiskTypes.PC]: "#10225B",
  [RiskTypes.SS]: "#1C359A",
  [RiskTypes.HC]: "#6E81D0",
  [RiskTypes.AE]: "#5081E6",
};
const legendItems = Object.entries(riskTypeColors).map(([key, color]) => ({
  name: key,
  color,
}));

/**
 * LineChartContainer renders a responsive multi-line chart using Recharts
 * to visualize monthly data for various user types (AE, SS, HCO, PTQ).
 *
 * It includes a custom legend and dual X-axes (month and year).
 *
 * @component
 * @param {Object} props
 * @param {ChartData[]} props.data - The data array containing values for each month and year.
 *
 * @example
 * const data = [
 *   { month: "Jan", year: 2023,  SS: 8, HCO: 5, PTQ: 7 },
 *   ...
 * ];
 *
 * <LineChartContainer data={data} />
 */
const LineChartContainer: React.FC<LineChartContainerProps> = ({ data }) => {
  return (
    <>
      <CustomLegend items={legendItems} itemsPerRow={4} />
      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={data}
          margin={{
            top: 50,
            right: 10,
            left: -20,
            bottom: -10,
          }}
        >
          <CartesianGrid
            horizontal={true}
            vertical={false}
            strokeDasharray="3 3"
          />
          {/* X-Axis (Months & Years) */}
          <XAxis
            dataKey="xLabel1"
            tickLine={false}
            xAxisId="xLabel1Axis"
            tick={{ fontSize: 12, fill: "#626262" }}
          />
          <XAxis
            dataKey="xLabel"
            tickLine={false}
            axisLine={false}
            tick={{ dy: 0, fontSize: 12, fill: "#626262" }}
            xAxisId="xLabelAxis"
            allowDuplicatedCategory={false}
          />
          <YAxis
            tickLine={false}
            tick={{ fontSize: 12, fill: "#626262" }}
            allowDecimals={false}
          />
          <Tooltip />

          {Object.entries(riskTypeColors).map(([key, color]) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={color}
              strokeWidth={2}
              dot={false}
              xAxisId="xLabel1Axis"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default LineChartContainer;
